<template>
  <div class="trip-table-wrapper">
    <base-loader v-if="tripStore.dataIsLoading" />

    <!-- Таблица -->
    <TableComponent
      v-else-if="
        !tripStore.dataIsLoading &&
        tableColumns.length > 0 &&
        tripStore.trips.length > 0
      "
      :data="tripStore.trips"
      :columns="tableColumns"
      :selectable="isMainView"
      :multiple="isMainView"
      :ready="!tripStore.dataIsLoading"
      @selection-change="handleSelectionChange"
      @row-middle-click="
        openRouteInNewTab({
          name: 'TripDetails',
          params: { id: $event.id },
        })
      "
      @row-click="handleRowClick"
      @vehicle-link-click="goToVehicleDetails"
      @driver-link-click="goToDriverDetails"
    >
      <template #cell-status="{ item }">
        <BaseStatusSelect
          compact
          :value="item.status"
          :label="tripStore.formatTripStatus(item.status)"
          :color="tripStore.tripStatusColor(item.status)"
          :options="tripStore.tripStatusSelectOptions"
          @change="handleStatusChange(item, $event)"
        />
      </template>
    </TableComponent>

    <div v-else class="no-data">Рейсов не найдено</div>

    <!-- Пагинация -->
    <PaginationComponent
      v-if="
        tripStore.pagination.pages > 1 &&
        !tripStore.dataIsLoading &&
        tableColumns.length > 0
      "
      :page="tripStore.pagination.page"
      :pages="tripStore.pagination.pages"
      :series="tripStore.pagination.series"
      :route-query="currentRouteQuery"
      @update:page="handlePageChange"
    />
  </div>
</template>

<script>
import TableComponent from "@/components/TableComponent.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";

import { useTripStore } from "@/stores/tripStore";
import { useTableConfigStore } from "@/stores/tableConfigStore";
import { useNotificationStore } from "@/stores/notificationStore";

import { updateUrlWithFilters } from "@/utils/filterUtils";
import { openRouteInNewTab } from "@/utils/routeUtils";

export default {
  name: "TripTable",
  components: {
    TableComponent,
    PaginationComponent,
  },
  props: {
    modelId: {
      type: [String, Number],
      default: null,
    },
    modelType: {
      type: String,
      default: null,
      validator: (value) =>
        !value || ["driver", "vehicle"].includes(value),
    },
  },
  data() {
    return {
      tableColumns: [],
    };
  },
  computed: {
    tripStore() {
      return useTripStore();
    },
    tableConfigStore() {
      return useTableConfigStore();
    },
    notificationStore() {
      return useNotificationStore();
    },
    currentRouteQuery() {
      return this.$route.query;
    },
    isMainView() {
      return !this.modelId;
    },
    filterKey() {
      return this.modelType === "driver" ? "driver_id" : "vehicle_id";
    },
  },
  watch: {
    modelId: {
      handler() {
        if (!this.isMainView) this.fetchRelatedTrips();
      },
      immediate: false,
    },
  },
  methods: {
    async loadTableConfig() {
      try {
        const columns =
          await this.tableConfigStore.initTableConfiguration(
            "Trip",
            "trips"
          );
        this.tableColumns = columns.filter(
          (col) => col.enabled !== false
        );
      } catch (error) {
        console.error(
          "Не удалось загрузить конфигурацию таблицы рейсов:",
          error
        );
        try {
          const { loadTableColumns } = await import(
            "@/utils/tableFormatter"
          );
          this.tableColumns = await loadTableColumns("trips");
        } catch (fallbackError) {
          console.error("Ошибка загрузки таблицы:", fallbackError);
          this.tableColumns = [];
        }
      }
    },

    async fetchRelatedTrips(page = 1) {
      if (this.isMainView) return;

      const params = {
        page: page,
        "q[enabled]": "true",
        [`q[${this.filterKey}]`]: this.modelId,
      };

      await this.tripStore.fetchTrips(params, this.tableColumns);
    },

    handleRowClick(item) {
      const tripId = item.id || item;
      this.$router.push({
        name: "TripDetails",
        params: { id: tripId },
      });
    },

    handleSelectionChange(selected) {
      this.$emit("selection-change", selected);
    },

    handlePageChange(page) {
      if (this.isMainView) {
        updateUrlWithFilters(this.$router, {}, page);
      } else {
        this.fetchRelatedTrips(page);
      }
    },

    goToVehicleDetails(vehicleId) {
      this.$router.push({
        name: "VehicleDetails",
        params: { id: vehicleId },
      });
    },

    goToDriverDetails(driverId) {
      this.$router.push({
        name: "DriverDetails",
        params: { id: driverId },
      });
    },

    openRouteInNewTab,

    // Обновляет статус рейса прямо из строки таблицы
    async handleStatusChange(trip, status) {
      try {
        await this.tripStore.updateStatus(trip.id, status);
        trip.status = status;
        this.notificationStore.showSuccess("Статус рейса обновлён");
      } catch {
        // ошибка перехватывается глобально в apiService
      }
    },
  },
  async mounted() {
    await this.loadTableConfig();

    if (!this.isMainView) {
      await this.fetchRelatedTrips();
    }
  },
};
</script>

<style scoped>
.trip-table-wrapper {
  width: 100%;
}

.no-data {
  margin-top: var(--base-out-indent);
}
</style>
